import { useState, useCallback, useMemo, useRef, useContext } from 'react'
import PropTypes from 'prop-types'

import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/comments/lib/constants'

import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { questionIdToPosition } from '@aytm/app/stats/page/components/lib/utils'
import { questionByPosition, getQuestionsOptionsList } from '@aytm/app/stats/page/comments/lib/utils'

import { OEContext } from '@aytm/app/stats/page/comments'

import SurveyHeaderComments from '@aytm/app/stats/page/comments/comments/components/SurveyHeaderComments'
import PageDataComments from '@aytm/app/stats/page/comments/comments/PageDataComments'
import useFetchDataComments from '@aytm/app/stats/page/comments/comments/hooks/useFetchDataComments'
import useComments from '@aytm/app/stats/page/comments/comments/hooks/useComments'
import useTags from '@aytm/app/stats/page/comments/comments/hooks/useTags'
import useCodeBooks from '@aytm/app/stats/page/comments/comments/hooks/useCodeBooks'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

// styles
import '@aytm/app/stats/page/comments/styles/keyframes.scss'

import style from '@aytm/app/stats/page/comments/styles/index.module.scss'

const VideoResponses = props => {
  const {
    specialMode,
    surveyData,
    reportId,
    apiKey,
    sentimentOptions,
    reportParams = {},
    reportVirtual,
    reportSchema,
    loadRejectedCS,
    isShowHiddenFilters,
    isAdvancedFiltersDisabled,
    isSpecialsSortingDisabled = false,
    onFetch,
    onTagsFetch,
    onUpdateParams,
    onUpdateSchema,
    onFetchSchema,
    onFetchApiKey,
    onCreateFilter,
    onTranslationChange,
    onQuestionsListChange,
    onSelectedQuestionChange,
    onQAModeChange
  } = props

  const { searchState, dispatch, dispatchCommentStatuses } = useContext(OEContext)

  const {
    pageData = [],
    tagsData,
    request,
    disabled: readOnly,
    isLoading,
    isProcessing,
    isShowSentiments
  } = searchState

  const [popupNotificationsDisabled, setPopupNotificationsDisabled] = useState({
    kudos: 'true' == String(window.localStorage.getItem('kudosNotificationsDisabled')) || false,
    unkudos: 'true' == String(window.localStorage.getItem('unkudosNotificationsDisabled')) || false,
    reject: 'true' == String(window.localStorage.getItem('rejectNotificationsDisabled')) || false
  })

  const [survey, setSurvey] = useState(
    Object.assign({}, surveyData.survey || {}, {
      traits: convertTraits(surveyData.survey || {}),
      codeBooks: (surveyData.survey || {}).codeBooks || []
    })
  )
  const [currentResponse, setCurrentResponse] = useState(null)
  const [responsesOptions, setResponsesOptions] = useState({
    survey,
    reportParams,
    reportVirtual,
    reportSchema,
    selectedQuestion: reportParams.selectedQuestion || DEFAULT_SELECTED_QUESTION
  })

  const responsesRef = useRef([])
  const showTraitsRef = useRef({})
  const responseOrderRef = useRef({})

  const searchStateRef = useRef()
  searchStateRef.current = searchState

  const isSpecialsSortingDisabledRef = useRef()
  isSpecialsSortingDisabledRef.current = isSpecialsSortingDisabled

  const propsRef = useRef()
  propsRef.current = props

  const surveyRef = useRef()
  surveyRef.current = survey

  const responsesOptionsRef = useRef()
  responsesOptionsRef.current = responsesOptions

  const questionsList = useMemo(
    () =>
      getQuestionsOptionsList(
        responsesOptions.survey,
        responsesOptions.reportVirtual,
        responsesOptions.reportSchema,
        responsesOptions.selectedQuestion,
        specialMode
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      responsesOptions.survey.questions,
      responsesOptions.survey.scripts,
      responsesOptions.reportVirtual,
      responsesOptions.reportSchema,
      responsesOptions.selectedQuestion,
      specialMode
    ]
  )
  const question = useMemo(
    () =>
      questionsList[0].questionNumber
        ? questionByPosition(questionIdToPosition(questionsList[0].questionNumber))
        : null,
    [questionsList]
  )

  const onPageChange = (page, uidsList = null) => {
    const _request = {
      currentPage: page
    }
    if (uidsList) {
      _request.uidsToApprove = uidsList
    }
    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: true } })
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request: _request
    })
  }

  const onCloseNotification = useCallback(
    type => {
      setPopupNotificationsDisabled({
        ...popupNotificationsDisabled,
        [type]: true
      })
      window.localStorage.setItem(`${type}NotificationsDisabled`, true)
    },
    [popupNotificationsDisabled]
  )

  const { fetchData } = useFetchDataComments({
    apiKey,
    dispatch,
    dispatchCommentStatuses,
    searchStateRef,
    propsRef,
    responsesOptionsRef,
    surveyData,
    survey,
    setSurvey,
    specialMode,
    onFetchSchema,
    onFetch,
    onFetchApiKey,
    onQAModeChange
  })

  useComments({
    dispatch,
    searchStateRef,
    propsRef,
    responsesOptionsRef,
    surveyData,
    survey,
    specialMode,
    sentimentOptions,
    questionsList,
    isShowSentiments,
    isSpecialsSortingDisabledRef,
    reportParams,
    responsesRef,
    reportVirtual,
    reportSchema,
    pageData,
    setSurvey,
    setResponsesOptions,
    onQuestionsListChange,
    onUpdateSchema
  })

  const [_onTagsUpdateSchema, _onTagsFetch] = useTags({
    survey,
    tagsData,
    request,
    dispatch,
    setSurvey,
    onFetch: fetchData,
    onTagsFetch,
    onUpdateSchema
  })

  const [_onCodeBooksFetch] = useCodeBooks({
    survey,
    tagsData,
    request,
    dispatch,
    setSurvey,
    onFetch: fetchData,
    onTagsFetch
  })

  useEventListener('tagsFetch', event => {
    if (event.detail) {
      const { data, updated } = event.detail
      _onTagsFetch(data, updated)
    }
  })

  useEventListener('codeBooksFetch', event => {
    if (event.detail) {
      const { data, updated } = event.detail
      _onCodeBooksFetch(data, updated)
    }
  })

  return (
    <>
      <div className={`${style['comments-container']} stats-Comments`}>
        <SurveyHeaderComments
          survey={survey}
          readOnly={readOnly}
          specialMode={specialMode}
          pageData={responsesOptions.pageData}
          questionsList={questionsList}
          showTraitsRef={showTraitsRef}
          responseOrderRef={responseOrderRef}
          currentResponse={currentResponse}
          loadRejectedCS={loadRejectedCS}
          isVirtualOrXpertInScope={false}
          isOnlyVirtualOrXpert={false}
          isAdvancedFiltersDisabled={isAdvancedFiltersDisabled}
          isSpecialsSortingDisabled={isSpecialsSortingDisabled}
          onCreateFilter={onCreateFilter}
          onTranslationChange={onTranslationChange}
          onSelectedQuestionChange={onSelectedQuestionChange}
          onUpdateSchema={_onTagsUpdateSchema}
          onPageChange={onPageChange}
          fetchData={fetchData}
        />
        {question && !isLoading && !isProcessing ? (
          <div className={style['video-question']}>
            <div className={style['video-question-title']}>
              <strong>Q{question.visual_position}: </strong>
              <span dangerouslySetInnerHTML={{ __html: question.text }} />
            </div>
            <div
              className={style['video-question-instruction']}
              dangerouslySetInnerHTML={{ __html: question.va_instructions }}
            />
          </div>
        ) : null}
        <PageDataComments
          reportId={reportId}
          apiKey={apiKey}
          survey={survey}
          responsesOptions={responsesOptions}
          setResponsesOptions={setResponsesOptions}
          responsesRef={responsesRef}
          showTraitsRef={showTraitsRef}
          responseOrderRef={responseOrderRef}
          currentResponse={currentResponse}
          isVirtualOrXpertInScope={false}
          isOnlyVirtualOrXpert={false}
          isShowHiddenFilters={isShowHiddenFilters}
          popupNotificationsDisabled={popupNotificationsDisabled}
          setCurrentResponse={setCurrentResponse}
          onCloseNotification={onCloseNotification}
          onUpdateParams={onUpdateParams}
          onUpdateSchema={_onTagsUpdateSchema}
          onPageChange={onPageChange}
        />
      </div>
    </>
  )
}

VideoResponses.propTypes = {
  specialMode: PropTypes.string,
  surveyData: PropTypes.object,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  apiKey: PropTypes.object,
  questionId: PropTypes.string,
  loadRejectedCS: PropTypes.string,
  sentimentOptions: PropTypes.object,
  reportParams: PropTypes.object,
  reportVirtual: PropTypes.object,
  reportSchema: PropTypes.array,
  reports: PropTypes.array,
  isShowHiddenFilters: PropTypes.bool,
  isAdvancedFiltersDisabled: PropTypes.bool,
  isSpecialsSortingDisabled: PropTypes.bool,
  onFetch: PropTypes.func,
  onTagsFetch: PropTypes.func,
  onFetchSchema: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onCreateFilter: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onTranslationChange: PropTypes.func,
  onQuestionsListChange: PropTypes.func,
  onSelectedQuestionChange: PropTypes.func,
  onQAModeChange: PropTypes.func
}

VideoResponses.displayName = 'VideoResponses'

export default VideoResponses
